import React, { useState } from 'react';
import {
  AI_RESEARCH_SKILL_SALARY_IMPACT,
  AI_RESEARCH_LABS,
  SKILL_CATEGORIES
} from '../data/aiResearchSkills';

// Color scale for salary impact
const getHeatColor = (value, max = 55000) => {
  const intensity = Math.min(value / max, 1);
  if (intensity > 0.8) return '#b91c1c'; // Deep red - highest impact
  if (intensity > 0.6) return '#dc2626'; // Red
  if (intensity > 0.4) return '#f59e0b'; // Orange/gold
  if (intensity > 0.2) return '#fbbf24'; // Yellow
  return '#fef3c7'; // Light yellow
};

// Get text color based on background
const getTextColor = (value, max = 55000) => {
  const intensity = value / max;
  return intensity > 0.5 ? '#fff' : '#374151';
};

const SkillHeatmap = ({
  userSkills = [],
  selectedLabs = ['openai', 'anthropic', 'deepmind', 'metaFair'],
  viewMode = 'salary', // 'salary' | 'demand'
  onSkillClick
}) => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const userSkillSet = new Set(userSkills);

  // Group skills by category
  const skillsByCategory = Object.entries(AI_RESEARCH_SKILL_SALARY_IMPACT).reduce((acc, [skill, data]) => {
    const cat = data.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ skill, ...data });
    return acc;
  }, {});

  // Sort categories by average impact
  const sortedCategories = Object.keys(SKILL_CATEGORIES).filter(cat => skillsByCategory[cat]);

  // Get cell value based on view mode
  const getCellValue = (skillData, labKey) => {
    const lab = AI_RESEARCH_LABS[labKey];
    const isTopSkill = lab?.topSkills?.includes(skillData.skill);

    if (viewMode === 'demand') {
      return isTopSkill ? 100 : skillData.demandScore;
    }
    // Salary mode - boost if it's a top skill for the lab
    return isTopSkill ? skillData.impact * 1.2 : skillData.impact;
  };

  const labs = selectedLabs.map(key => AI_RESEARCH_LABS[key]).filter(Boolean);

  return (
    <div className="card" style={{ marginBottom: '20px', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e5e5',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      }}>
        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#fff' }}>
          AI Research Lab Skill Heatmap
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#9ca3af' }}>
          Skills mapped by salary impact across top AI labs. Your skills shown with checkmarks.
        </p>
      </div>

      {/* Heatmap Container */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: '600px', padding: '16px' }}>
          {/* Lab Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `180px repeat(${labs.length}, 1fr)`,
            gap: '2px',
            marginBottom: '8px'
          }}>
            <div style={{ padding: '8px', fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
              SKILL
            </div>
            {labs.map(lab => (
              <div
                key={lab.shortName}
                style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: lab.color,
                  background: `${lab.color}10`,
                  borderRadius: '4px'
                }}
              >
                {lab.shortName}
              </div>
            ))}
          </div>

          {/* Skills by Category */}
          {sortedCategories.map(category => (
            <div key={category} style={{ marginBottom: '12px' }}>
              {/* Category Header */}
              <div style={{
                padding: '6px 8px',
                fontSize: '10px',
                fontWeight: '600',
                color: SKILL_CATEGORIES[category].color,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                background: `${SKILL_CATEGORIES[category].color}10`,
                borderRadius: '4px',
                marginBottom: '4px'
              }}>
                {SKILL_CATEGORIES[category].label}
              </div>

              {/* Skills in Category */}
              {skillsByCategory[category]
                .sort((a, b) => b.impact - a.impact)
                .map(skillData => {
                  const hasSkill = userSkillSet.has(skillData.skill);
                  return (
                    <div
                      key={skillData.skill}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: `180px repeat(${labs.length}, 1fr)`,
                        gap: '2px',
                        marginBottom: '2px'
                      }}
                    >
                      {/* Skill Name */}
                      <div
                        style={{
                          padding: '8px',
                          fontSize: '11px',
                          color: hasSkill ? '#059669' : '#374151',
                          fontWeight: hasSkill ? '600' : '400',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: hasSkill ? 'rgba(5, 150, 105, 0.08)' : 'transparent',
                          borderRadius: '4px'
                        }}
                      >
                        {hasSkill && <span style={{ color: '#059669' }}>✓</span>}
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {skillData.skill}
                        </span>
                      </div>

                      {/* Lab Cells */}
                      {labs.map(lab => {
                        const value = getCellValue(skillData, Object.keys(AI_RESEARCH_LABS).find(k => AI_RESEARCH_LABS[k] === lab));
                        const isTopSkill = lab.topSkills?.includes(skillData.skill);
                        const cellKey = `${skillData.skill}-${lab.shortName}`;
                        const isHovered = hoveredCell === cellKey;

                        return (
                          <div
                            key={lab.shortName}
                            onMouseEnter={() => setHoveredCell(cellKey)}
                            onMouseLeave={() => setHoveredCell(null)}
                            onClick={() => onSkillClick?.(skillData.skill, lab)}
                            style={{
                              padding: '8px 4px',
                              textAlign: 'center',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: getHeatColor(value),
                              color: getTextColor(value),
                              borderRadius: '4px',
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                              boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                              border: isTopSkill ? `2px solid ${lab.color}` : '2px solid transparent',
                              position: 'relative'
                            }}
                          >
                            +${(skillData.impact / 1000).toFixed(0)}K
                            {isTopSkill && (
                              <span style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                fontSize: '8px',
                                background: lab.color,
                                color: '#fff',
                                borderRadius: '50%',
                                width: '14px',
                                height: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                ★
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e5e5',
        background: '#f9fafb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Color Scale */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '10px', color: '#6b7280' }}>Salary Impact:</span>
          <div style={{ display: 'flex', gap: '2px' }}>
            {['#fef3c7', '#fbbf24', '#f59e0b', '#dc2626', '#b91c1c'].map((color, i) => (
              <div
                key={color}
                style={{
                  width: '24px',
                  height: '12px',
                  background: color,
                  borderRadius: i === 0 ? '2px 0 0 2px' : i === 4 ? '0 2px 2px 0' : '0'
                }}
              />
            ))}
          </div>
          <span style={{ fontSize: '10px', color: '#6b7280' }}>+$55K</span>
        </div>

        {/* Icons Legend */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <span style={{ fontSize: '10px', color: '#059669', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>✓</span> You have this skill
          </span>
          <span style={{ fontSize: '10px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{
              background: '#3b82f6',
              color: '#fff',
              borderRadius: '50%',
              width: '12px',
              height: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '7px'
            }}>★</span>
            Top skill for lab
          </span>
        </div>
      </div>
    </div>
  );
};

export default SkillHeatmap;
